import {inject, Injectable} from "@angular/core";
import {PaddockStore} from "src/app/domain/assets/paddocks/paddock.store";
import {FarmApiClient} from "src/app/infrastructure/moovement-api/web-api/farm.api-client";
import {DataStorageService} from "src/app/infrastructure/data-storage.service";
import {Paddock, SynchronizationStatus} from "src/app/infrastructure/moovement-api/models/asset";
import {PaddockApiClient} from "src/app/infrastructure/moovement-api/web-api/paddock.api-client";
import {
  InternalNotificationsStore,
  InternalNotificationType
} from "src/app/infrastructure/internal-notifications/internal-notifications.store";
import {AnimalsService} from "src/app/domain/assets/animals/animals.service";
import {DeviceAssociationStore} from "src/app/domain/devices/device-association.store";
import {DataType} from "src/app/domain/domain-models";

@Injectable({
  providedIn: 'root'
})
export class PaddockService {

  private deviceAssociationsStore = inject(DeviceAssociationStore);
  private internalNotificationsStore = inject(InternalNotificationsStore);
  private paddockStore = inject(PaddockStore);

  constructor(
    private animalService: AnimalsService,
    private farmApiClient: FarmApiClient,
    private dataStorageService: DataStorageService,
    private paddockApiClient: PaddockApiClient
  ) {}

  reset() {
    this.paddockStore.reset();
  }

  loadPaddocks(farmId: string): void {
    this.farmApiClient.getPaddocks(farmId).subscribe({
      next: paddocks => this.savePaddocks(farmId, paddocks),
      error: async () => {
        const cachedPaddocks = await this.dataStorageService.getAllActiveAssetsMatching<Paddock>(DataType.paddocks, farmId);
        this.paddockStore.setAllPaddocks(cachedPaddocks);
      }
    });
  }

  async savePaddocks(farmId: string, paddocks: Paddock[]): Promise<void> {
    for (const paddock of paddocks) {
      await this.dataStorageService.saveAsset(DataType.paddocks, farmId, paddock, SynchronizationStatus.upToDate);
    }
    this.paddockStore.setAllPaddocks(paddocks);
  }

  updatePaddock(farmId: string, paddock: Paddock): void {
    this.paddockApiClient.update(paddock).subscribe({
      next: async paddock => {
        await this.dataStorageService.saveAsset(DataType.paddocks, farmId, paddock, SynchronizationStatus.upToDate)
        this.paddockStore.setPaddock(paddock);
        this.internalNotificationsStore.addNotification("Successfully updated paddock.", InternalNotificationType.SUCCESS, false);
      },
      error: _ => {
        this.internalNotificationsStore.addNotification("Failed to update paddock.", InternalNotificationType.ERROR, false);
      }
    });
  }

  async deletePaddock(farmId: string, paddockId: string): Promise<void> {
    this.paddockApiClient.delete(paddockId).subscribe({
      next: async () => {
        await this.dataStorageService.deleteAsset(paddockId);
        this.paddockStore.removePaddock(paddockId);
        this.animalService.loadAnimals(farmId, this.deviceAssociationsStore.entities());
        this.internalNotificationsStore.addNotification("Successfully deleted paddock.", InternalNotificationType.SUCCESS, false);
      },
      error: _ => {
        this.internalNotificationsStore.addNotification("Failed to delete paddock.", InternalNotificationType.ERROR, false);
      }
    });
  }
}
